import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Download, AlertTriangle, RefreshCw, CheckCircle, Info } from 'lucide-react'
import type { PackageBuildStatus } from '../../services/collection-packages'
import { formatBytes } from './format-bytes'

export function PackageBuildHeader({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mb-4 flex items-center justify-between">
      <h3 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">
        {t('collections.packageBuild.title')}
      </h3>
      <button
        onClick={onClose}
        className="rounded-lg p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
        aria-label={t('collections.packageBuild.close')}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

export function NoTemplatePanel(): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div
      className="mb-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/50"
      role="alert"
    >
      <Info className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
      <p className="text-sm text-amber-700 dark:text-amber-400">
        {t('collections.packageBuild.noTemplate')}
      </p>
    </div>
  )
}

export function NoTargetsPanel(): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div
      className="mb-6 space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/50"
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="space-y-2 text-sm text-amber-700 dark:text-amber-400">
          <p>{t('collections.packageBuild.noTargets')}</p>
          <p>{t('collections.packageBuild.noTargetsCollectorOnly')}</p>
          <p>{t('collections.packageBuild.noTargetsEditRequest')}</p>
        </div>
      </div>
    </div>
  )
}

export function BuildingSpinner(): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mb-6 flex flex-col items-center gap-3 py-4" role="status">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-fuchsia-600 border-t-transparent" />
      <span className="text-sm text-slate-600 dark:text-slate-400">
        {t('collections.packageBuild.buildingPackage')}
      </span>
    </div>
  )
}

export function ReadyPanel({ status }: { status: PackageBuildStatus }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mb-6 space-y-3 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/50">
      <p className="font-heading text-sm font-semibold text-green-800 dark:text-green-400">
        {t('collections.packageBuild.packageReady')}
      </p>
      {status.packageHash && (
        <p className="font-mono text-xs break-all text-green-700 dark:text-green-400">
          {t('collections.packageBuild.packageHash', { hash: status.packageHash })}
        </p>
      )}
      {status.packageSizeBytes && (
        <p className="text-xs text-green-700 dark:text-green-400">
          {t('collections.packageBuild.packageSize', {
            size: formatBytes(status.packageSizeBytes),
          })}
        </p>
      )}
    </div>
  )
}

export function DownloadSuccessPanel(): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mb-6 flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/50">
      <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
      <p className="text-sm text-green-700 dark:text-green-400">
        {t('collections.packageBuild.downloadSuccess')}
      </p>
    </div>
  )
}

export function ErrorPanel({ error }: { error: string | null }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div
      className="mb-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/50"
      role="alert"
    >
      <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
      <p className="text-sm text-red-700 dark:text-red-400">
        {error
          ? t('collections.packageBuild.buildFailedDetail', { message: error })
          : t('collections.packageBuild.buildFailed')}
      </p>
    </div>
  )
}

const PRIMARY_BTN =
  'flex items-center gap-2 rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-medium text-white hover:bg-fuchsia-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-fuchsia-500'

interface BuildActionsProps {
  hasTemplate: boolean
  isReady: boolean
  isFailed: boolean
  isBuilding: boolean
  hasError: boolean
  building: boolean
  downloading: boolean
  downloadComplete: boolean
  targetCount: number
  onClose: () => void
  onBuild: () => void
  onBuildCollectorOnly: () => void
  onDownload: () => void
}

function CloseButton({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <button
      onClick={onClose}
      className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
    >
      {t('collections.packageBuild.close')}
    </button>
  )
}

function RetryButton({
  onBuild,
  building,
}: {
  onBuild: () => void
  building: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <button onClick={onBuild} disabled={building} className={PRIMARY_BTN}>
      <RefreshCw className="h-4 w-4" />
      {t('collections.packageBuild.retryBuild')}
    </button>
  )
}

function DownloadButton({
  onDownload,
  downloading,
}: {
  onDownload: () => void
  downloading: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <button onClick={onDownload} disabled={downloading} className={PRIMARY_BTN}>
      <Download className="h-4 w-4" />
      {t('collections.packageBuild.downloadPackage')}
    </button>
  )
}

function BuildStartButton({
  onBuild,
  building,
}: {
  onBuild: () => void
  building: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <button onClick={onBuild} disabled={building} className={PRIMARY_BTN}>
      {t('collections.packageBuild.buildButton')}
    </button>
  )
}

function BuildCollectorOnlyButton({
  onClick,
  building,
}: {
  onClick: () => void
  building: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <button onClick={onClick} disabled={building} className={PRIMARY_BTN}>
      {t('collections.packageBuild.buildCollectorOnly')}
    </button>
  )
}

function isIdle(props: BuildActionsProps): boolean {
  return !props.isReady && !props.isFailed && !props.isBuilding && !props.hasError
}

function pickIdleAction(props: BuildActionsProps): ReactElement {
  if (props.targetCount > 0) {
    return <BuildStartButton onBuild={props.onBuild} building={props.building} />
  }
  return <BuildCollectorOnlyButton onClick={props.onBuildCollectorOnly} building={props.building} />
}

function pickPrimaryAction(props: BuildActionsProps): ReactElement | null {
  if (!props.hasTemplate) return null
  if (props.isFailed || props.hasError) {
    return <RetryButton onBuild={props.onBuild} building={props.building} />
  }
  if (props.isReady && !props.downloadComplete) {
    return <DownloadButton onDownload={props.onDownload} downloading={props.downloading} />
  }
  return isIdle(props) ? pickIdleAction(props) : null
}

export function BuildActions(props: BuildActionsProps): ReactElement {
  return (
    <div className="flex justify-end gap-3">
      <CloseButton onClose={props.onClose} />
      {pickPrimaryAction(props)}
    </div>
  )
}
